/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchCategoryViewProps, ISearchFormViewProps, ISearchKeywordViewProps, ISearchProductViewProps } from './components';
import { ISearchViewProps } from './dlvry-search';
import { useBlackoutOverlay } from './components/dlvry-search.hooks';
import { IDlvrySearchConfig } from './dlvry-search.props.autogenerated';
import { format } from '@msdyn365-commerce/retail-proxy';

const SearchView: React.FC<ISearchViewProps> = props => {
    const {
        context,
        Search,
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        // @ts-expect-error
        KeywordSuggest,
        ProductSuggest,
        CategorySuggest,
        // @ts-expect-error
        UlKeyword,
        UlProduct,
        UlCategory,
        form,
        autosuggestCategory,
        // @ts-expect-error
        autosuggestKeyword,
        autosuggestProduct,
        SearchForm,
        FormWrapper,
        isLoadingAutoSuggest,
        isLoadingNode,
        searchTermCompleted,
        showPopup,
        scannerModal,
        callbacks: { handleCancelSearchFocused }
    } = props;

    const [isMobilePort, setIsMobile] = React.useState<boolean>(false);
    const overlayDivRef = React.useRef<HTMLDivElement | null>(null);
    const searchDivRef = React.useRef<HTMLDivElement | null>(null);
    const [
        shouldShowOverlay,
        popupPosition,
        setIsInputFocused
    ] = useBlackoutOverlay(props, searchDivRef, isMobilePort, Boolean(searchText), true);

    React.useEffect(() => {
        const onResize = () => {
            const isMobileView = isMobile({ variant: VariantType.Browser, context: context.request }) === 'xs';
            setIsMobile(isMobileView);
        };
        onResize(); // Call to set initial value

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [context]);

    return (
        <>
        <Module {...Search} data-focused={shouldShowOverlay ? true : undefined} ref={searchDivRef}>
            {_renderForm(props.config, form as ISearchFormViewProps, SearchForm, FormWrapper, searchText, searchTermCompleted, scannerModal)}
            {showPopup && (
                <Node {...AutoSuggest} style={popupPosition}>
                    {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}

                    {searchTermCompleted && (
                        <Node className='msc-autoSuggest__results-container'>
                            {/* {_renderKeywordSuggestions(KeywordSuggest, UlKeyword, autosuggestKeyword, isLoadingAutoSuggest, isLoadingNode)} */}
                            {_renderCategorySuggestions(CategorySuggest, UlCategory, autosuggestCategory, isLoadingAutoSuggest, isLoadingNode)}
                            {_renderProductSuggestions(ProductSuggest, UlProduct, autosuggestProduct, isLoadingAutoSuggest, isLoadingNode)}
                            {!isLoadingAutoSuggest && !autosuggestCategory && !autosuggestProduct && (
                                <div className='msc-autoSuggest__noResults'>
                                    <div className='msc-autoSuggest__noResults__logo' />
                                    <div className='msc-autoSuggest__noResults__heading'>
                                        {props.resources.noResultsPopupHeading} <span>&quot;{searchTermCompleted}&quot;</span>
                                    </div>
                                    <div className='msc-autoSuggest__noResults__text'>
                                        {props.resources.noResultsPopupFirstText}
                                    </div>
                                    <div className='msc-autoSuggest__noResults__text'>
                                        {props.resources.noResultsPopupSecondText}
                                    </div>
                                </div>
                            )}
                            {!isLoadingAutoSuggest && (
                                <div className='ms-search__autoSuggest__footer'>
                                    <button className='ms-search__autoSuggest__footer__search-link' type='submit' form='searchForm'>
                                        {format(props.resources.searchSuggestionsFooterText, searchTermCompleted)}
                                    </button>
                                </div>
                            )}
                        </Node>
                    )}
                </Node>
            )}
        </Module>
        {shouldShowOverlay && (
            <div ref={overlayDivRef} className='ms-search__overlay' onClick={(event) => {
                if (event.target === overlayDivRef.current) {
                    setIsInputFocused(false);
                    handleCancelSearchFocused?.();
                }
            }} />
        )}
        </>
    );
};

const _renderForm = (
    config: IDlvrySearchConfig,
    form: ISearchFormViewProps,
    SearchForm: INodeProps,
    FormWrapper: INodeProps,
    searchText: string | undefined,
    searchTermCompleted: string | undefined,
    scannerModal: React.ReactNode
) => {
    return (
        <Node {...SearchForm}>
            <Node {...FormWrapper}>
                {form.submitBtn}
                {form.input}
                {config.enableBarcodeScanner && form.scannerBtn}
                {scannerModal}
            </Node>
        </Node>
    );
};

// @ts-expect-error
const _renderKeywordSuggestions = (
    KeywordSuggest: INodeProps,
    UlKeyword: INodeProps,
    keywordSuggestions?: ISearchKeywordViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        keywordSuggestions && (
            <Node {...KeywordSuggest}>
                <Node {...UlKeyword}>
                    {/* {isLoadingAutoSuggest && isLoadingNode} */}
                    {!isLoadingAutoSuggest &&
                        keywordSuggestions.text.map(text => {
                            return text;
                        })}
                </Node>
            </Node>
        )
    );
};

const _renderProductSuggestions = (
    ProductSuggest: INodeProps,
    UlProduct: INodeProps,
    productSuggestions?: ISearchProductViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        productSuggestions && (
            <Node {...ProductSuggest}>
                <Node {...UlProduct}>
                    {productSuggestions.title}
                    {isLoadingAutoSuggest && isLoadingNode}
                    {!isLoadingAutoSuggest &&
                        productSuggestions.items.map((item, index) => {
                            return (
                                <Node {...item.LiProduct} key={item.id || index}>
                                    <Node {...item.AProduct}>
                                        <div className='msc-autoSuggest__productResults-item-image'>
                                            {item.thumbnail}
                                        </div>
                                        <div className='msc-autoSuggest__productResults-item-middle' draggable onDragStart={e => e.preventDefault()} onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                                            {item.text}
                                            {item.quantity}
                                            {item.availability}
                                            {/* {item.buySection} */}
                                        </div>
                                        <div className='msc-autoSuggest__productResults-item-right' draggable onDragStart={e => e.preventDefault()} onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                                            {item.price}
                                            {item.totalPrice}
                                            {item.addToCart}
                                        </div>
                                        {/* <div className='msc-autoSuggest__productResults-item-info'>
                                            <div className='msc-autoSuggest__productResults-item-info-bottom'>
                                            </div>
                                        </div> */}
                                    </Node>
                                </Node>
                            );
                        })}
                </Node>
            </Node>
        )
    );
};

const _renderCategorySuggestions = (
    CategorySuggest: INodeProps,
    UlCategory: INodeProps,
    categorySuggestions?: ISearchCategoryViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        categorySuggestions && (
            <Node {...CategorySuggest}>
                <Node {...UlCategory}>
                    {categorySuggestions.title}
                    {isLoadingAutoSuggest && isLoadingNode}
                    {!isLoadingAutoSuggest &&
                        categorySuggestions.text.map(text => {
                            return text;
                        })}
                </Node>
            </Node>
        )
    );
};

export default SearchView;
